import { api } from './api';
import {
  ESF,
  ESFAdmin,
  ICSPosition,
  ICSPositionAdmin,
  PositionAssignment,
  UserCurrentPosition,
  PositionSettings,
  QualifiedPosition,
  PositionRequest,
  CreateESFRequest,
  UpdateESFRequest,
  CreateICSPositionRequest,
  UpdateICSPositionRequest,
  CreateAssignmentRequest,
  UpdateAssignmentRequest,
} from '@/types/position';

interface ApiResponse<T> {
  data: T;
}

// ESF endpoints
export const esfService = {
  getAll: () => api.get<ApiResponse<ESF[]>>('/positions/esfs').then(res => res.data),

  getById: (id: string) => api.get<ApiResponse<ESF>>(`/positions/esfs/${id}`).then(res => res.data),

  create: (data: CreateESFRequest) =>
    api.post<ApiResponse<ESF>>('/positions/esfs', data).then(res => res.data),

  update: (id: string, data: UpdateESFRequest) =>
    api.patch<ApiResponse<ESF>>(`/positions/esfs/${id}`, data).then(res => res.data),

  getAdmin: () =>
    api.get<ApiResponse<ESFAdmin[]>>('/positions/esfs/admin').then(res => res.data),

  reset: (id: string) =>
    api.post<ApiResponse<void>>(`/positions/esfs/${id}/reset`).then(res => res.data),

  deactivate: (id: string) =>
    api.post<ApiResponse<ESFAdmin>>(`/positions/esfs/${id}/deactivate`).then(res => res.data),
};

// ICS Position endpoints
export const icsPositionService = {
  getAll: (section?: string) =>
    api
      .get<ApiResponse<ICSPosition[]>>('/positions/ics-positions', section ? { section } : undefined)
      .then(res => res.data),

  getById: (id: string) =>
    api.get<ApiResponse<ICSPosition>>(`/positions/ics-positions/${id}`).then(res => res.data),

  create: (data: CreateICSPositionRequest) =>
    api.post<ApiResponse<ICSPosition>>('/positions/ics-positions', data).then(res => res.data),

  update: (id: string, data: UpdateICSPositionRequest) =>
    api.patch<ApiResponse<ICSPosition>>(`/positions/ics-positions/${id}`, data).then(res => res.data),

  getAdmin: () =>
    api.get<ApiResponse<ICSPositionAdmin[]>>('/positions/ics-positions/admin').then(res => res.data),

  reset: (id: string) =>
    api.post<ApiResponse<void>>(`/positions/ics-positions/${id}/reset`).then(res => res.data),

  deactivate: (id: string) =>
    api.post<ApiResponse<ICSPositionAdmin>>(`/positions/ics-positions/${id}/deactivate`).then(res => res.data),

  reorder: (positions: { id: string; sort_order: number }[]) =>
    api.post<ApiResponse<void>>('/positions/ics-positions/reorder', { positions }).then(res => res.data),
};

// Position Assignment endpoints
export const assignmentService = {
  getByIncident: (incidentId: string, currentOnly: boolean = true) =>
    api
      .get<ApiResponse<PositionAssignment[]>>(
        `/positions/incidents/${incidentId}/assignments`,
        { current: currentOnly }
      )
      .then(res => res.data),

  getById: (id: string) =>
    api.get<ApiResponse<PositionAssignment>>(`/positions/assignments/${id}`).then(res => res.data),

  create: (data: CreateAssignmentRequest) =>
    api.post<ApiResponse<PositionAssignment>>('/positions/assignments', data).then(res => res.data),

  update: (id: string, data: UpdateAssignmentRequest) =>
    api
      .patch<ApiResponse<PositionAssignment>>(`/positions/assignments/${id}`, data)
      .then(res => res.data),

  relieve: (id: string) =>
    api
      .post<ApiResponse<PositionAssignment>>(`/positions/assignments/${id}/relieve`)
      .then(res => res.data),

  delete: (id: string) => api.delete(`/positions/assignments/${id}`),
};

// User's current position within an incident
export const myPositionService = {
  get: (incidentId: string) =>
    api.get<ApiResponse<UserCurrentPosition>>(`/positions/incidents/${incidentId}/my-position`).then(res => res.data),

  // Cross-incident: one row per active assignment for the calling user.
  // Used to gate the Supply Request Approval Queue's Approve buttons.
  getAllCurrent: () =>
    api
      .get<ApiResponse<Array<{ incident_id: string; position_code: string | null }>>>(
        '/positions/my-current-positions'
      )
      .then(res => res.data),
};

// Position self-assignment endpoints
export const positionSelfAssignService = {
  getQualified: (incidentId: string) =>
    api.get<ApiResponse<QualifiedPosition[]>>(`/positions/incidents/${incidentId}/qualified-positions`).then(res => res.data),
  selfAssign: (incidentId: string, data: { ics_position_id?: string; esf_id?: string; custom_title?: string; notes?: string }) =>
    api.post<ApiResponse<PositionAssignment>>(`/positions/incidents/${incidentId}/self-assign`, data).then(res => res.data),
  createRequest: (incidentId: string, data: { ics_position_id?: string; esf_id?: string; custom_title?: string; notes?: string }) =>
    api.post(`/positions/incidents/${incidentId}/requests`, data),
  getRequests: (incidentId: string) =>
    api.get<ApiResponse<PositionRequest[]>>(`/positions/incidents/${incidentId}/requests`).then(res => res.data),
  getMyRequests: (incidentId: string) =>
    api.get<ApiResponse<PositionRequest[]>>(`/positions/incidents/${incidentId}/my-requests`).then(res => res.data),
  reviewRequest: (requestId: string, data: { action: 'approve' | 'deny'; notes?: string }) =>
    api.post(`/positions/requests/${requestId}/review`, data),
  cancelRequest: (requestId: string) =>
    api.delete(`/positions/requests/${requestId}`),
  switch: (incidentId: string, data: { ics_position_id?: string; esf_id?: string; relieve_current: boolean }) =>
    api.post<ApiResponse<PositionAssignment>>(`/positions/incidents/${incidentId}/switch`, data).then(res => res.data),
};

// Position settings endpoints
export const positionSettingsService = {
  get: () =>
    api.get<ApiResponse<PositionSettings>>('/positions/settings/positions').then(res => res.data),
  update: (data: Partial<PositionSettings>) =>
    api.put<ApiResponse<PositionSettings>>('/positions/settings/positions', data).then(res => res.data),
};
